import { Injectable } from '@angular/core';
import {
  ColDef,
  GridOptions,
  ICellRendererParams,
  ValueGetterParams,
  ExcelStyle,
  ExcelAlignment,
  CellStyle,
  CellClassParams,
  ITooltipParams,
  ValueFormatterParams,
} from 'ag-grid-enterprise';
import { Formatter } from '@aptitude/shared-vanilla';
import { map, take } from 'rxjs';

import { GridTemplateService } from '../grid-template.service';
import { CustomOverlayNoRowsComponent } from '../overlay-no-rows/overlay-no-rows.component';
import { GridTooltipComponent } from '../grid-tooltip/grid-tooltip.component';
import { GridClinicalAssessmentWrapperComponent } from '../grid-clinical-assessment-wrapper/grid-clinical-assessment-wrapper.component';
import { Identifier } from '../../../../types/identifiers/identifier';
import {
  CrossReference,
  Product,
  ProductCategory,
} from '../../../../types/product';
import {
  ApplicationRoutesService,
  Route,
} from '../../../../services/application-routes.service';
import { CategoryStructureTooltipComponent } from '../category-structure-tooltip/category-structure-tooltip.component';
import { ScopeStatus } from '../../../../types/rfp';
import { ColumnSize } from '../../../modules';
import { Features } from '../../../../types/types';
import { ProductDetailRouteService } from '../../../../services/product-detail-route.service';
import { FeatureListService } from '../../../../services/feature-list.service';
import { ProductDescriptionCellRendererComponent } from '../cell-renderers/product-description-cell-renderer.component';
import {
  ProductIdCellRendererComponent,
  ProductIdCellRendererComponentParams,
} from '../cell-renderers/product-id-cell-renderer.component';
import { CrossReferenceTypeCellRendererComponent } from '../cell-renderers/cross-reference-type-cell-renderer.component';
import {
  SellerCellRendererComponent,
  SellerCellRendererComponentParams,
} from '../cell-renderers/seller-cell-renderer.component';
import {
  CategoryCellRendererWrapperComponent,
  CategoryPathCellRendererComponentParams,
} from '../category-cell-renderer-wrapper/category-cell-renderer-wrapper.component';

export enum IColumnsTypes {
  PRODUCT = 'product',
  DESCRIPTION = 'description',
  SELLER = 'seller',
  MANUFACTURER = 'manufacturer',
  DISTRIBUTOR = 'distributor',
  PACKAGING = 'packaging',
  CATEGORY = 'category',
  PARTICIPANT_COUNT = 'participantCount',
  UNITS = 'units',
  ANNUALIZED_UNITS = 'annualizedUnits',
  ACTUAL_SPEND = 'actualSpend',
  ANNUALIZED_SPEND = 'annualizedSpend',
  CARVE_IN_SPEND_STYLE = 'carveInSpendStyle',
  CROSS_ID = 'crossId',
  CROSS_DESCRIPTION = 'crossDescription',
  CROSS_TYPE = 'crossType',
  BRAND_NAME = 'brandName',
  MANUFACTURER_PRODUCT_ID = 'manufacturerProductId',
  DISTRIBUTOR_PRODUCT_ID = 'distributorProductId',
}

export type IColumnTypes = Record<IColumnsTypes, ColDef>;

export abstract class IGridColumnDefinitionsService {
  abstract getDefaultColumnTypes: () => IColumnTypes;
  abstract getGridOptions: () => GridOptions;
}

export interface IProductValueGetter<T> extends ValueGetterParams {
  data: T;
}

export interface IProductTooltipParams<T> extends ITooltipParams {
  data?: T;
}

export interface ICellClassParams<T> extends CellClassParams {
  data: T;
}

export interface ProductWithSpend extends Product {
  seller: Identifier;
  category: ProductCategory;
  bestCross?: CrossReference;
  spendRecord: ContractSpendOnProduct;
}

interface ContractSpendOnProduct extends ContractSpend {
  sellerId: number;
  categoryId: number;
  participantCount: number;
  masked: boolean;
}

interface ContractSpend {
  aggId: number;
  spend: number;
  units: number;
}

@Injectable({
  providedIn: 'root',
})
export class GridColumnDefinitionsService
  implements IGridColumnDefinitionsService
{
  private features?: Features;
  constructor(
    private gridTemplateService: GridTemplateService,
    private productDetailRoute: ProductDetailRouteService,
    private featuresService: FeatureListService,
    public applicationRoutes: ApplicationRoutesService,
  ) {
    this.featuresService.aptitudeFeatureList$
      .pipe(
        map((features) => (this.features = features)),
        take(1),
      )
      .subscribe();
  }

  public getDefaultColumnTypes(features?: Features): IColumnTypes {
    return {
      [IColumnsTypes.PRODUCT]: {
        colId: IColumnsTypes.PRODUCT,
        headerName: 'Product',
        cellClass: 'stringExport',
        comparator: (v1: string, v2: string) => {
          const valueA = v1.toLowerCase();
          const valueB = v2.toLowerCase();
          if (valueA === valueB) {
            return 0;
          }
          return valueA > valueB ? 1 : -1;
        },
        valueGetter: (params: IProductValueGetter<Product>) => {
          return params.data.catalogNumber;
        },
        tooltipValueGetter: (params: ITooltipParams) => {
          return params.data.catalogNumber;
        },
        cellRenderer: ProductIdCellRendererComponent,
        cellRendererParams: ({
          data,
        }: ICellRendererParams<Product>): ProductIdCellRendererComponentParams => ({
          status: data?.status,
          titleUrl: this.applicationRoutes.get(
            Route.PRODUCT_DETAIL,
            data?.id || NaN,
          ),
        }),
      },
      [IColumnsTypes.DESCRIPTION]: {
        colId: IColumnsTypes.DESCRIPTION,
        headerName: 'Description',
        valueGetter: (params: IProductValueGetter<Product>) => {
          return params.data.description;
        },
        cellRenderer: ProductDescriptionCellRendererComponent,
      },
      [IColumnsTypes.SELLER]: {
        colId: IColumnsTypes.SELLER,
        headerName: 'Seller',
        valueGetter: (params: IProductValueGetter<Product>) => {
          const { seller, sysSeller } = params.data;
          if (sysSeller) {
            // for ag-grid to include the sysSeller in the search
            return seller.name + ' ' + sysSeller.name;
          }
          return seller.name;
        },
        cellRenderer: SellerCellRendererComponent,
        cellRendererParams: ({
          data,
        }: ICellRendererParams<Product>): SellerCellRendererComponentParams => ({
          manufacturerName: data?.sysSeller.name || '',
          sellerName: data?.seller.name || '',
        }),
      },
      [IColumnsTypes.MANUFACTURER]: {
        colId: IColumnsTypes.MANUFACTURER,
        headerName: 'Manufacturer',
        valueGetter: (params: IProductValueGetter<Product>) => {
          const { manufacturer, parentManufacturer } = params.data;
          if (parentManufacturer) {
            // for ag-grid to include the sysSeller in the search
            return manufacturer.name + ' ' + parentManufacturer.name;
          }
          return manufacturer.name;
        },
        cellRenderer: SellerCellRendererComponent,
        cellRendererParams: ({
          data,
        }: ICellRendererParams<Product>): SellerCellRendererComponentParams => ({
          manufacturerName: data?.parentManufacturer?.name || '',
          sellerName: data?.manufacturer?.name || '',
        }),
      },
      [IColumnsTypes.DISTRIBUTOR]: {
        colId: IColumnsTypes.DISTRIBUTOR,
        headerName: 'Distributor',
        valueGetter: (params: IProductValueGetter<Product>) => {
          const { distributor, parentDistributor } = params.data;
          if (parentDistributor) {
            // for ag-grid to include the sysSeller in the search
            return `${distributor?.name} ${parentDistributor.name}`;
          }
          return distributor?.name;
        },
        cellRenderer: SellerCellRendererComponent,
        cellRendererParams: ({
          data,
        }: ICellRendererParams<Product>): SellerCellRendererComponentParams => ({
          manufacturerName: data?.parentDistributor?.name || '',
          sellerName: data?.distributor?.name || '',
        }),
      },
      [IColumnsTypes.PACKAGING]: {
        colId: IColumnsTypes.PACKAGING,
        headerName: 'Packaging String',
        columnGroupShow: 'closed',
        valueGetter: (params: IProductValueGetter<Product>) =>
          params.data.packageString,
      },
      [IColumnsTypes.CATEGORY]: {
        colId: IColumnsTypes.CATEGORY,
        headerName: 'Category',
        valueGetter: (params: IProductValueGetter<Product>) =>
          params.data.categories[0].name,
        cellRenderer: CategoryCellRendererWrapperComponent,
        cellRendererParams: (
          params: ICellRendererParams,
        ): CategoryPathCellRendererComponentParams => {
          const category = params.data?.categories[0] as ProductCategory;
          return {
            category,
          };
        },
        ...CategoryStructureTooltipComponent.columnPropsForComponent(
          (params: { data?: Product }): Identifier[] => {
            const product = params.data;
            if (!product) {
              return [];
            }
            const categories = [
              ...product.categories[0].parents,
              {
                name: product.categories[0].name,
                id: product.categories[0].id,
              },
            ];
            return categories;
          },
        ),
      },
      [IColumnsTypes.PARTICIPANT_COUNT]: {
        colId: IColumnsTypes.PARTICIPANT_COUNT,
        headerName: 'Systems',
        type: 'numericColumn',
        filter: 'agNumberColumnFilter',
        valueGetter: (params: IProductValueGetter<ProductWithSpend>) =>
          params.data.spendRecord.participantCount,
        valueFormatter: (params: ValueFormatterParams) =>
          Formatter.number(params.value),
        maxWidth: ColumnSize.MEDIUM,
      },
      [IColumnsTypes.UNITS]: {
        colId: IColumnsTypes.UNITS,
        headerComponentParams: {
          template:
            this.gridTemplateService.agGridColumnHeaderTemplateForTwoLineText(),
        },
        headerName: 'Actual Units',
        type: 'numericColumn',
        filter: 'agNumberColumnFilter',
        cellStyle: this.getSpendUnitsStyle(),
        valueGetter: (params: IProductValueGetter<Product>) =>
          params.data?.spend?.actualSpend.unitVolume,
        valueFormatter: (params: ValueFormatterParams) =>
          Formatter.number(params.value),
        maxWidth: ColumnSize.MEDIUM,
      },
      [IColumnsTypes.ANNUALIZED_UNITS]: {
        colId: IColumnsTypes.ANNUALIZED_UNITS,
        headerComponentParams: {
          template:
            this.gridTemplateService.agGridColumnHeaderTemplateForTwoLineText(),
        },
        headerName: 'Annualized Units',
        type: 'numericColumn',
        filter: 'agNumberColumnFilter',
        cellStyle: this.getSpendUnitsStyle(),
        valueGetter: (params: IProductValueGetter<Product>) =>
          params.data?.spend?.annualizedSpend?.unitVolume,
        valueFormatter: (params: ValueFormatterParams) =>
          Formatter.number(params.value),
        maxWidth: ColumnSize.MEDIUM,
      },
      [IColumnsTypes.ACTUAL_SPEND]: {
        colId: IColumnsTypes.ACTUAL_SPEND,
        headerComponentParams: {
          template:
            this.gridTemplateService.agGridColumnHeaderTemplateForTwoLineText(),
        },
        headerName: 'Actual Spend',
        type: 'numericColumn',
        filter: 'agNumberColumnFilter',
        cellStyle: this.getSpendUnitsStyle(),
        filterValueGetter: ({ data }: { data: Product }) =>
          Math.round(data.spend?.actualSpend?.spendVolume || 0),
        valueGetter: (params: IProductValueGetter<Product>) =>
          params.data?.spend?.actualSpend.spendVolume,
        tooltipValueGetter: (params: ITooltipParams) => {
          return params.value === 0
            ? 'Zero spend data found for this product'
            : '';
        },
        valueFormatter: (params: ValueFormatterParams) =>
          Formatter.currency(params.value),
        maxWidth: ColumnSize.MEDIUM,
      },
      [IColumnsTypes.ANNUALIZED_SPEND]: {
        colId: IColumnsTypes.ANNUALIZED_SPEND,
        headerComponentParams: {
          template:
            this.gridTemplateService.agGridColumnHeaderTemplateForTwoLineText(),
        },
        headerName: 'Annualized Spend',
        type: 'numericColumn',
        filter: 'agNumberColumnFilter',
        cellStyle: this.getSpendUnitsStyle(),
        filterValueGetter: ({ data }: { data: Product }) =>
          Math.round(data.spend?.annualizedSpend?.spendVolume || 0),
        valueGetter: (params: IProductValueGetter<Product>) =>
          params.data?.spend?.annualizedSpend?.spendVolume,
        tooltipValueGetter: (params: ITooltipParams) => {
          return params.value === 0
            ? 'Zero spend data found for this product'
            : '';
        },
        valueFormatter: (params: ValueFormatterParams) =>
          Formatter.currency(params.value),
        maxWidth: ColumnSize.MEDIUM,
      },
      [IColumnsTypes.CARVE_IN_SPEND_STYLE]: {
        colId: IColumnsTypes.CARVE_IN_SPEND_STYLE,
        cellStyle: (params) => {
          const cellStyle: CellStyle = this.getSpendUnitsStyle();
          if (params.data.scopeStatus === ScopeStatus.CARVE_IN) {
            cellStyle['color'] = 'purple';
          }
          return cellStyle;
        },
      },
      [IColumnsTypes.CROSS_ID]: {
        colId: IColumnsTypes.CROSS_ID,
        headerName: 'Cross Product',
        columnGroupShow: 'closed',
        cellClass: 'stringExport',
        valueGetter: (params: IProductValueGetter<ProductWithSpend>) =>
          params.data.bestCross?.toProduct.catalogNumber || '',
        tooltipValueGetter: (params: ITooltipParams) => {
          return `${params.value}`;
        },
        cellRenderer: ProductIdCellRendererComponent,
        cellRendererParams: ({
          data,
        }: ICellRendererParams<ProductWithSpend>): ProductIdCellRendererComponentParams => ({
          status: data?.bestCross?.toProduct.status,
          titleUrl: this.productDetailRoute.get(
            {
              categorizationSchemeId: data?.categories[0].parents[0].id || NaN,
              productSource: data?.bestCross?.toProduct.productSource || '',
              productId: data?.bestCross?.toProduct.id || NaN,
              categoryId: data?.bestCross?.toProduct.aptitudeCategory.id || NaN,
            },
            { features: this.features },
          ),
        }),
      },
      [IColumnsTypes.CROSS_DESCRIPTION]: {
        colId: IColumnsTypes.CROSS_DESCRIPTION,
        headerName: 'Cross Description',
        columnGroupShow: 'closed',
        valueGetter: (params: IProductValueGetter<ProductWithSpend>) =>
          params.data.bestCross?.toProduct.description || '',
        cellRenderer: ProductDescriptionCellRendererComponent,
      },
      [IColumnsTypes.CROSS_TYPE]: {
        colId: IColumnsTypes.CROSS_TYPE,
        headerName: 'Cross Type',
        columnGroupShow: 'open',
        headerClass: 'ag-alternate-background-color',
        valueGetter: ({ data }: { data: ProductWithSpend }): string => {
          if (!data.bestCross) {
            return 'Not Crossed';
          }
          return data.bestCross?.crossReferenceDescription;
        },
        cellRenderer: CrossReferenceTypeCellRendererComponent,
      },
      [IColumnsTypes.BRAND_NAME]: {
        colId: IColumnsTypes.BRAND_NAME,
        headerName: 'Brand Name',
        columnGroupShow: 'open',
        valueGetter: (params: IProductValueGetter<Product>) => {
          return params.data.brandName;
        },
      },
      [IColumnsTypes.MANUFACTURER_PRODUCT_ID]: {
        colId: IColumnsTypes.MANUFACTURER_PRODUCT_ID,
        headerName: 'Manufacturer Product ID',
        showRowGroup: false,
        rowGroup: false,
        cellRenderer: 'agGroupCellRenderer',
        valueGetter: (params: IProductValueGetter<Product>) =>
          params.data?.hierarchy?.length === 2
            ? ''
            : params.data?.catalogNumber,
      },
      [IColumnsTypes.DISTRIBUTOR_PRODUCT_ID]: {
        colId: IColumnsTypes.DISTRIBUTOR_PRODUCT_ID,
        headerName: 'Distributor Product ID',
        valueGetter: (params: IProductValueGetter<Product>) =>
          params.data?.hierarchy?.length === 2
            ? params.data?.catalogNumber
            : '',
      },
    };
  }

  public getGridOptions(): GridOptions {
    return {
      columnTypes: this.getDefaultColumnTypes(),
      accentedSort: true,
      defaultColDef: {
        filter: true,
        flex: 1,
        tooltipComponent: 'gridTooltipComponent',
      },
      excelStyles: [
        {
          id: 'bold',
          font: { bold: true },
        },
        {
          id: 'stringExport',
          dataType: 'String',
          alignment: {
            horizontal: 'Left',
          } as ExcelAlignment,
        } as ExcelStyle,
      ] as ExcelStyle[],
      rowHeight: 75,
      icons: {
        groupExpanded:
          '<i class="fas fa-lg fa-minus-circle text-secondary"></i>',
        groupContracted:
          '<i class="fas fa-lg fa-plus-circle text-success"></i>',
      },
      suppressColumnVirtualisation: true,
      components: {
        ...CategoryStructureTooltipComponent.frameworkComponentProp(),
        customOverlayNoRowsComponent: CustomOverlayNoRowsComponent,
        gridTooltipComponent: GridTooltipComponent,
        gridClinicalAssessmentWrapperComponent:
          GridClinicalAssessmentWrapperComponent,
      },
      noRowsOverlayComponent: 'customOverlayNoRowsComponent',
    };
  }

  private getSpendUnitsStyle(): CellStyle {
    return { textAlign: 'right' };
  }
}
